import React, { useState, useMemo } from "react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

// Type-safe icon wrappers
const ChevronLeftIcon = FiChevronLeft as React.FC<React.SVGProps<SVGSVGElement>>;
const ChevronRightIcon = FiChevronRight as React.FC<React.SVGProps<SVGSVGElement>>;

interface DateCalendarProps {
  selectedDate: Date | null;
  onDateChange: (date: Date) => void;
}

const DAYS_OF_WEEK = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/**
 * DateCalendar - Calendar date picker
 */
const DateCalendar: React.FC<DateCalendarProps> = ({
  selectedDate,
  onDateChange,
}) => {
  const today = new Date();
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [viewYear, setViewYear] = useState(today.getFullYear());

  // Calculate calendar days
  const calendarDays = useMemo(() => {
    const firstDayOfMonth = new Date(viewYear, viewMonth, 1);
    const lastDayOfMonth = new Date(viewYear, viewMonth + 1, 0);
    const startingDayOfWeek = firstDayOfMonth.getDay();
    const daysInMonth = lastDayOfMonth.getDate();

    const days: (number | null)[] = [];

    // Add empty slots for days before the first day
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return days;
  }, [viewMonth, viewYear]);

  const handlePrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(viewYear - 1);
    } else {
      setViewMonth(viewMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
    } else {
      setViewMonth(viewMonth + 1);
    }
  };

  const handleDateClick = (day: number) => {
    const date = new Date(viewYear, viewMonth, day);
    // Don't allow selecting past dates
    if (date < new Date(today.getFullYear(), today.getMonth(), today.getDate())) {
      return;
    }
    onDateChange(date);
  };

  const isSelectedDate = (day: number): boolean => {
    if (!selectedDate) return false;
    return (
      selectedDate.getDate() === day &&
      selectedDate.getMonth() === viewMonth &&
      selectedDate.getFullYear() === viewYear
    );
  };

  const isToday = (day: number): boolean => {
    return (
      today.getDate() === day &&
      today.getMonth() === viewMonth &&
      today.getFullYear() === viewYear
    );
  };

  const isPastDate = (day: number): boolean => {
    const date = new Date(viewYear, viewMonth, day);
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return date < todayStart;
  };

  return (
    <div className="su-section su-date-calendar">
      {/* Header */}
      <div className="su-calendar-header">
        <span className="su-calendar-label">SELECT DATE</span>
        <div className="su-calendar-nav">
          <button
            className="su-calendar-nav-btn"
            onClick={handlePrevMonth}
            aria-label="Previous month"
          >
            <ChevronLeftIcon className="w-4 h-4" />
          </button>
          <span className="su-calendar-month">
            {MONTHS[viewMonth]} {viewYear}
          </span>
          <button
            className="su-calendar-nav-btn"
            onClick={handleNextMonth}
            aria-label="Next month"
          >
            <ChevronRightIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Days of Week Header */}
      <div className="su-calendar-weekdays">
        {DAYS_OF_WEEK.map((day) => (
          <div key={day} className="su-calendar-weekday">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="su-calendar-grid">
        {calendarDays.map((day, index) => (
          <div key={index} className="su-calendar-cell">
            {day !== null && (
              <button
                className={`su-calendar-day ${isSelectedDate(day) ? "selected" : ""} ${
                  isToday(day) ? "today" : ""
                } ${isPastDate(day) ? "past" : ""}`}
                onClick={() => handleDateClick(day)}
                disabled={isPastDate(day)}
              >
                {day}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default DateCalendar;


